/**
 * AdminContext.jsx
 * 
 * Context for Admin Portal state management with:
 * 1. On-demand data fetching (only when Admin tab is accessed)
 * 2. Admin-specific state isolation
 * 3. Optional chaining throughout to prevent 'ghost logouts'
 * 
 * Reference Data (CPT codes, providers, locations) stays in StaticDataContext
 * This context handles MANAGEMENT operations and UI state
 */

import React, { createContext, useContext, useState, useCallback } from 'react';
import axios from 'axios';
import { useUserRoleName } from '../hooks/useUserRoleName';

// Must include `/api` suffix — kubernetes ingress routes any `/api/*` path to
// the FastAPI backend on :8001, while any other path falls through to the React
// frontend on :3000. Without `/api` these requests 404.
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminContext = createContext(null);

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
};

export const AdminProvider = ({ children, user }) => {
  const userRoleName = useUserRoleName();
  // ============================================
  // Loading States
  // ============================================
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingAuditLogs, setLoadingAuditLogs] = useState(false);
  const [loadingRoutingRules, setLoadingRoutingRules] = useState(false);
  const [loadingServiceTypes, setLoadingServiceTypes] = useState(false);
  
  // ============================================
  // Admin Data (fetched on-demand)
  // ============================================
  const [auditLogs, setAuditLogs] = useState([]);
  const [routingRules, setRoutingRules] = useState([]);
  const [reminderSettings, setReminderSettings] = useState(null);
  const [passwordResetRequests, setPasswordResetRequests] = useState([]);
  const [clientLabels, setClientLabels] = useState([]);
  const [waitlistCommunicationSettings, setWaitlistCommunicationSettings] = useState(null);
  const [paymentReminderSettings, setPaymentReminderSettings] = useState(null);
  
  // ============================================
  // Admin UI State (dialogs, forms)
  // ============================================
  const [adminDataLoaded, setAdminDataLoaded] = useState(false);
  const [currentAdminDialogDirty, setCurrentAdminDialogDirty] = useState(false);
  
  // ============================================
  // Admin Data Fetchers (on-demand)
  // ============================================
  
  /**
   * Fetch audit logs - Admin only
   * Uses optional chaining for safety
   */
  const fetchAuditLogs = useCallback(async (filters = {}) => {
    // Safe permission check with optional chaining
    if (!user?.is_super_admin && userRoleName !== 'super admin') {
      void 0 && console.log('[AdminContext] User not authorized for audit logs');
      return;
    }
    
    setLoadingAuditLogs(true);
    try {
      // Backend endpoint is GET /api/audit-logs/recent with query params:
      //   limit, resource_type, user_id, action.
      // The old code hit /audit-logs?... which returned 404 on every admin
      // panel load. Date-range filters aren't yet supported server-side —
      // only user/action/resource filters. Add date-range backend support
      // in a follow-up if billers request it.
      const queryParts = [];
      if (filters?.actionType) queryParts.push(`action=${encodeURIComponent(filters.actionType)}`);
      if (filters?.userId) queryParts.push(`user_id=${encodeURIComponent(filters.userId)}`);
      if (filters?.resourceType) queryParts.push(`resource_type=${encodeURIComponent(filters.resourceType)}`);
      queryParts.push('limit=200');
      const queryString = `?${queryParts.join('&')}`;
      const response = await axios.get(`${API}/audit-logs/recent${queryString}`);
      setAuditLogs(response?.data || []);
    } catch (error) {
      console.error('[AdminContext] Error fetching audit logs:', error);
      setAuditLogs([]);
    } finally {
      setLoadingAuditLogs(false);
    }
  }, [user?.is_super_admin, userRoleName]);
  
  /**
   * Fetch routing rules - Admin only
   */
  const fetchRoutingRules = useCallback(async () => {
    if (!user?.is_super_admin && userRoleName !== 'super admin') {
      return;
    }
    
    setLoadingRoutingRules(true);
    try {
      const response = await axios.get(`${API}/routing-rules`);
      setRoutingRules(response?.data || []);
    } catch (error) {
      console.error('[AdminContext] Error fetching routing rules:', error);
      setRoutingRules([]);
    } finally {
      setLoadingRoutingRules(false);
    }
  }, [user?.is_super_admin, userRoleName]);
  
  /**
   * Fetch reminder settings
   */
  const fetchReminderSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/reminder-settings`);
      setReminderSettings(response?.data || null);
    } catch (error) {
      console.error('[AdminContext] Error fetching reminder settings:', error);
      setReminderSettings(null);
    }
  }, []);
  
  /**
   * Fetch password reset requests - Super Admin only
   */
  const fetchPasswordResetRequests = useCallback(async () => {
    // Safe permission check
    if (!user?.is_super_admin && userRoleName !== 'super admin') {
      return;
    }
    
    try {
      const response = await axios.get(`${API}/admin/password-reset-requests`);
      setPasswordResetRequests(response?.data || []);
    } catch (error) {
      console.error('[AdminContext] Error fetching password reset requests:', error);
      setPasswordResetRequests([]);
    }
  }, [user?.is_super_admin, userRoleName]);
  
  /**
   * Fetch client labels
   */
  const fetchClientLabels = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/client-labels`);
      setClientLabels(response?.data || []);
    } catch (error) {
      console.error('[AdminContext] Error fetching client labels:', error);
      setClientLabels([]);
    }
  }, []);
  
  /**
   * Fetch waitlist communication settings
   */
  const fetchWaitlistCommunicationSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/waitlist-communication-settings`);
      setWaitlistCommunicationSettings(response?.data || null);
    } catch (error) {
      console.error('[AdminContext] Error fetching waitlist settings:', error);
      setWaitlistCommunicationSettings(null);
    }
  }, []);
  
  /**
   * Fetch payment reminder settings
   */
  const fetchPaymentReminderSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/payment-reminder-settings`);
      setPaymentReminderSettings(response?.data || null);
    } catch (error) {
      console.error('[AdminContext] Error fetching payment reminder settings:', error);
      setPaymentReminderSettings(null);
    }
  }, []);
  
  /**
   * Load all admin data - called when Admin tab is first accessed
   */
  const loadAdminData = useCallback(async () => {
    if (adminDataLoaded) {
      void 0 && console.log('[AdminContext] Admin data already loaded, skipping');
      return;
    }
    
    void 0 && console.log('[AdminContext] Loading admin data on-demand...');
    
    // Fetch data in parallel
    await Promise.all([
      fetchRoutingRules(),
      fetchReminderSettings(),
      fetchPasswordResetRequests(),
      fetchClientLabels(),
    ]);
    
    setAdminDataLoaded(true);
    void 0 && console.log('[AdminContext] Admin data loaded successfully');
  }, [
    adminDataLoaded, 
    fetchRoutingRules, 
    fetchReminderSettings, 
    fetchPasswordResetRequests, 
    fetchClientLabels
  ]);
  
  // ============================================
  // Admin CRUD Operations
  // ============================================
  
  /**
   * Save routing rule
   */
  const saveRoutingRule = useCallback(async (ruleData) => {
    try {
      if (ruleData?.id) {
        await axios.put(`${API}/routing-rules/${ruleData.id}`, ruleData);
      } else {
        await axios.post(`${API}/routing-rules`, ruleData);
      }
      await fetchRoutingRules();
      return { success: true };
    } catch (error) {
      console.error('[AdminContext] Error saving routing rule:', error);
      return { success: false, error: error?.response?.data?.detail || error?.message };
    }
  }, [fetchRoutingRules]);
  
  /**
   * Delete routing rule
   */
  const deleteRoutingRule = useCallback(async (ruleId) => {
    try {
      await axios.delete(`${API}/routing-rules/${ruleId}`);
      await fetchRoutingRules();
      return { success: true };
    } catch (error) {
      console.error('[AdminContext] Error deleting routing rule:', error);
      return { success: false, error: error?.response?.data?.detail || error?.message };
    }
  }, [fetchRoutingRules]);
  
  /**
   * Handle password reset request
   */
  const handlePasswordResetRequest = useCallback(async (requestId, action) => {
    try {
      await axios.post(`${API}/admin/password-reset-requests/${requestId}/${action}`);
      await fetchPasswordResetRequests();
      return { success: true };
    } catch (error) {
      console.error('[AdminContext] Error handling password reset:', error);
      return { success: false, error: error?.response?.data?.detail || error?.message };
    }
  }, [fetchPasswordResetRequests]);
  
  // ============================================
  // Context Value
  // ============================================
  const value = {
    // Loading states
    loadingUsers,
    loadingAuditLogs,
    loadingRoutingRules,
    loadingServiceTypes,
    
    // Data
    auditLogs,
    routingRules,
    reminderSettings,
    passwordResetRequests,
    clientLabels,
    waitlistCommunicationSettings,
    paymentReminderSettings,
    adminDataLoaded,
    
    // UI State
    currentAdminDialogDirty,
    setCurrentAdminDialogDirty,
    
    // Fetchers
    fetchAuditLogs,
    fetchRoutingRules,
    fetchReminderSettings,
    fetchPasswordResetRequests,
    fetchClientLabels,
    fetchWaitlistCommunicationSettings,
    fetchPaymentReminderSettings,
    loadAdminData,
    
    // CRUD operations
    saveRoutingRule,
    deleteRoutingRule,
    handlePasswordResetRequest,
    
    // Setters for external updates
    setAuditLogs,
    setRoutingRules,
    setReminderSettings,
    setClientLabels,
  };
  
  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
};

export default AdminContext;
